import React, { useMemo, useState } from "react";
import { FiCheck, FiEye, FiEyeOff, FiLock } from "react-icons/fi";
import SettingsHeader from "./SettingsHeader";

const LockIcon = FiLock;
const EyeIcon = FiEye;
const EyeOffIcon = FiEyeOff;
const CheckIcon = FiCheck;

const PasswordField = ({ label, value, onChange }) => {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOffIcon : EyeIcon;

  return (
    <label className="pp-settings-field pp-settings-password-field">
      <span>{label}</span>
      <span className="pp-settings-password-input-wrap">
        <input type={visible ? "text" : "password"} value={value} onChange={(event) => onChange(event.target.value)} />
        <button type="button" onClick={() => setVisible((current) => !current)} aria-label={visible ? "Hide password" : "Show password"}>
          <Icon aria-hidden="true" />
        </button>
      </span>
    </label>
  );
};

export default function PasswordSettingsScreen({ t, onBack }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const checks = useMemo(() => [
    {
      key: "length",
      label: t("parentPortal.settings.password_min_length", { defaultValue: "Minimum 8 characters" }),
      valid: newPassword.length >= 8,
    },
    {
      key: "uppercase",
      label: t("parentPortal.settings.password_uppercase", { defaultValue: "Minimum 1 uppercase letter" }),
      valid: /[A-Z]/.test(newPassword),
    },
    {
      key: "number",
      label: t("parentPortal.settings.password_number", { defaultValue: "Minimum 1 number" }),
      valid: /\d/.test(newPassword),
    },
  ], [newPassword, t]);

  const canSubmit = currentPassword && checks.every((check) => check.valid) && newPassword === confirmPassword;

  return (
    <div className="pp-settings-screen pp-settings-subscreen pp-settings-password-screen">
      <SettingsHeader
        t={t}
        onBack={onBack}
      />

      <section className="pp-settings-card pp-settings-form-card">
        <div className="pp-settings-card-heading">
          <span className="pp-settings-card-icon" aria-hidden="true">
            <LockIcon />
          </span>
          <div>
            <h3>{t("parentPortal.settings.change_password", { defaultValue: "Change password" })}</h3>
            <p>{t("parentPortal.settings.password_intro", { defaultValue: "Use a password that is hard to guess." })}</p>
          </div>
        </div>

        <form className="pp-settings-form" onSubmit={(event) => event.preventDefault()}>
          <PasswordField
            label={t("parentPortal.settings.current_password", { defaultValue: "Current password" })}
            value={currentPassword}
            onChange={setCurrentPassword}
          />
          <PasswordField
            label={t("parentPortal.settings.new_password", { defaultValue: "New password" })}
            value={newPassword}
            onChange={setNewPassword}
          />
          <PasswordField
            label={t("parentPortal.settings.confirm_new_password", { defaultValue: "Confirm new password" })}
            value={confirmPassword}
            onChange={setConfirmPassword}
          />

          <ul className="pp-settings-password-checks">
            {checks.map((check) => (
              <li key={check.key} className={check.valid ? "is-valid" : ""}>
                <CheckIcon aria-hidden="true" />
                {check.label}
              </li>
            ))}
          </ul>

          <div className="pp-settings-actions">
            <button type="submit" className="pp-settings-primary-button" disabled={!canSubmit}>
              {t("parentPortal.settings.change_password", { defaultValue: "Change password" })}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
